import React, { useEffect, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, Plus } from "lucide-react";

const SEVERITY_COLOR = {
  low: "text-emerald-500", medium: "text-amber-500", high: "text-orange-500", critical: "text-destructive",
};

export default function Safety() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ project_id: "default", type: "hazard", severity: "medium", description: "", location: "" });
  const load = () => apiClient().get("/safety/incidents").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await apiClient().post("/safety/incidents", form); toast.success("Incident logged"); setForm({ ...form, description: "", location: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const resolve = async (id) => { await apiClient().post(`/safety/incidents/${id}/resolve`); load(); };

  const open = items.filter((i) => i.status === "open");

  return (
    <div className="space-y-6 fade-in-up" data-testid="safety-page">
      <div>
        <h1 className="font-display text-5xl mt-1">Safety & Incident Log</h1>
      </div>

      {open.length > 0 && (
        <div className="border border-destructive/50 bg-destructive/5 overflow-hidden">
          <div className="overflow-hidden whitespace-nowrap">
            <div className="marquee inline-flex gap-8 py-2 px-3 text-sm">
              {[...open, ...open].map((i, idx) => (
                <span key={`${i.id}-${idx}`} className="inline-flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-destructive" />
                  <span className={`mono uppercase text-xs ${SEVERITY_COLOR[i.severity]}`}>[{i.severity}]</span>
                  <span>{i.type} · {i.location}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={add} className="border border-border bg-card p-5 grid md:grid-cols-5 gap-3" data-testid="incident-form">
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="incident-type" className="border border-border bg-background px-3 py-2 text-sm">
          {["hazard", "near_miss", "ppe_violation", "injury"].map(o => <option key={o}>{o}</option>)}
        </select>
        <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="incident-severity" className="border border-border bg-background px-3 py-2 text-sm">
          {["low", "medium", "high", "critical"].map(o => <option key={o}>{o}</option>)}
        </select>
        <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="incident-location" aria-label="Location" className="border border-border bg-background px-3 py-2 text-sm" />
        <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="incident-desc" aria-label="Description" className="md:col-span-1 border border-border bg-background px-3 py-2 text-sm" />
        <button data-testid="incident-add-btn" className="btn-sharp inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2"><Plus className="h-4 w-4" /> Log</button>
      </form>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-muted-foreground">No incidents.</div>}
        {items.map((i) => (
          <div key={i.id} className={`border p-4 flex items-start justify-between gap-4 ${i.status === "resolved" ? "border-border bg-card opacity-70" : "border-border bg-card"}`} data-testid={`incident-${i.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className={`mono text-xs uppercase tracking-widest ${SEVERITY_COLOR[i.severity]}`}>{i.severity}</span>
                <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{i.type}</span>
                <span className="mono text-[10px] text-muted-foreground">{new Date(i.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 font-heading">{i.description}</div>
              <div className="mono text-xs text-muted-foreground mt-1">📍 {i.location}</div>
            </div>
            {i.status === "open" ? (
              <button onClick={() => resolve(i.id)} data-testid={`resolve-${i.id}`} className="btn-sharp inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs hover:bg-muted">
                <CheckCircle2 className="h-3.5 w-3.5" /> Resolve
              </button>
            ) : (
              <span className="mono text-[10px] uppercase tracking-widest text-emerald-500">Resolved</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
